// hooks/useDynamicSystem.js
import { useState, useEffect } from 'react';
import { get, set } from 'idb-keyval';
import { axiosInstance } from '../services/axios';

export const SystemComponentCache = new Map();

export const useDynamicSystem = () => {
  const [isReady, setIsReady] = useState(SystemComponentCache.size > 0);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let loadedFromCache = false; // 🌟 Track if cache worked

    const loadSystem = async () => {
      let currentVersion = null;

      // 1. Check IndexedDB if Memory is empty
      if (SystemComponentCache.size === 0) {
        try {
          const idbData = await get('system-components');
          if (idbData && idbData.components) {
            currentVersion = idbData.version;
            Object.entries(idbData.components).forEach(([key, string]) => {
              SystemComponentCache.set(key, string);
            });
            loadedFromCache = true;
            if (isMounted) setIsReady(true); // 🌟 Mount instantly from cache!
          }
        } catch (err) {
          console.error("IndexedDB Error:", err);
        }
      } else {
        const idbData = await get('system-components');
        currentVersion = idbData ? idbData.version : null;
        loadedFromCache = true;
      }

      // 2. Background Revalidate (Fetch all updates)
      setIsUpdating(true);
      try {
        const versionParam = currentVersion ? `?version=${currentVersion}` : '';
        const response = await axiosInstance(`get-system-components.php${versionParam}`, { withCredentials: false });
        
        // Axios handles 200 OK
        if (response.status === 200 && response.data && response.data.components) {
          const newData = response.data;
          
          Object.entries(newData.components).forEach(([key, string]) => {
            SystemComponentCache.set(key, string);
          });
          
          await set('system-components', newData);
        }
      } catch (error) {
        // Axios throws an error for 304 Not Modified in some configurations, this safely catches it!
        if (error.response && error.response.status === 304) {
           console.log("System is up to date (304)");
        } else {
           console.error("Failed to sync system components", error);
        }
      } finally {
        if (isMounted) {
          setIsUpdating(false);
          // 🌟 ABSOLUTE FAILSAFE: Guarantee the app unlocks even if the network fails
          if (!isReady) setIsReady(true); 
        }
      }
    };

    loadSystem();
    return () => { isMounted = false; };
  }, []); // <-- Empty array is critical!

  return { isReady, isUpdating };
};